// Thin fetch wrapper: access token in memory, refresh cookie (httpOnly) rotates on 401, error codes from the shared catalogue (Guide §19).
import { ERRORS, type ErrorCode } from '@dd/shared';
import { API_BASE, APP_VERSION } from './env';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public messageBn: string, public messageEn: string, public details?: unknown, public requestId?: string) {
    super(code);
  }
  get isNetwork(): boolean { return this.status === 0; }
}

export interface ProfileDto {
  principalType: 'owner' | 'staff';
  principalId: string;
  accountId: string;
  shopId: string | null;
  role: 'owner' | 'manager' | 'cashier';
  name: string;
  language: 'bn' | 'en';
  mustChangePin: boolean;
  /** v10: the owner's profile picture */
  photoUrl?: string | null;
}
export interface SessionDto { accessToken: string; expiresIn: number; deviceCode?: string | null; profile: ProfileDto }

type Listener = (e: { epoch?: string; upgradeRequired?: boolean; unauthenticated?: boolean }) => void;
const listeners = new Set<Listener>();
export const onApiEvent = (l: Listener): (() => void) => { listeners.add(l); return () => listeners.delete(l); };
const emit: Listener = (e) => listeners.forEach((l) => l(e));

let accessToken: string | null = null;
let accessExp = 0;
let refreshing: Promise<SessionDto | null> | null = null;
/** why the last refresh failed: no network (stay offline-usable) vs the server said no (sign in again) */
export let refreshFailure: 'network' | 'auth' | null = null;
export const setAccessToken = (t: string | null, expiresIn = 900): void => { accessToken = t; accessExp = t ? Date.now() + (expiresIn - 20) * 1000 : 0; };
export const getAccessToken = (): string | null => accessToken;

async function raw(path: string, init: RequestInit & { json?: unknown; auth?: boolean; query?: Record<string, string | number | boolean | undefined | null> } = {}): Promise<Response> {
  const { json, auth = true, query, headers, ...rest } = init;
  let url = path.startsWith('http') || path.startsWith('/r/') ? path : `${API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  const h = new Headers(headers);
  h.set('x-app-version', APP_VERSION);
  if (json !== undefined) h.set('content-type', 'application/json');
  if (auth && accessToken) h.set('authorization', `Bearer ${accessToken}`);
  try {
    return await fetch(url, { credentials: 'include', ...rest, headers: h, body: json !== undefined ? JSON.stringify(json) : rest.body });
  } catch {
    throw new ApiError(0, 'network', 'ইন্টারনেট নেই। সংযোগ দেখুন।', 'No internet connection.');
  }
}

async function toError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; messageBn?: string; messageEn?: string; details?: unknown } } = {};
  try { body = await res.json(); } catch { /* not json */ }
  const e = body.error;
  const code = e?.code ?? (res.status === 426 ? 'upgrade_required' : 'internal');
  const def = (ERRORS as Record<string, { bn: string; en: string }>)[code];
  return new ApiError(res.status, code, e?.messageBn ?? def?.bn ?? 'কিছু একটা ভুল হয়েছে।', e?.messageEn ?? def?.en ?? 'Something went wrong.', e?.details, res.headers.get('x-request-id') ?? undefined);
}

export async function refreshSession(): Promise<SessionDto | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await raw('/auth/refresh', { method: 'POST', auth: false });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) { refreshFailure = 'auth'; setAccessToken(null); emit({ unauthenticated: true }); }
        else refreshFailure = 'network';
        return null;
      }
      refreshFailure = null;
      const s = (await res.json()) as SessionDto;
      setAccessToken(s.accessToken, s.expiresIn);
      return s;
    } catch {
      refreshFailure = 'network';
      return null;
    } finally {
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

export interface ReqOpts { json?: unknown; query?: Record<string, string | number | boolean | undefined | null>; auth?: boolean; signal?: AbortSignal; headers?: Record<string, string>; idempotencyKey?: string }

export async function request<T = unknown>(method: string, path: string, o: ReqOpts = {}): Promise<T> {
  const headers: Record<string, string> = { ...(o.headers ?? {}) };
  if (o.idempotencyKey) headers['idempotency-key'] = o.idempotencyKey;
  if ((o.auth ?? true) && !accessToken) await refreshSession();
  else if ((o.auth ?? true) && accessToken && Date.now() > accessExp) await refreshSession();
  let res = await raw(path, { method, json: o.json, query: o.query, auth: o.auth, signal: o.signal, headers });
  if (res.status === 401 && (o.auth ?? true) && !path.startsWith('/auth/')) {
    const s = await refreshSession();
    if (s) res = await raw(path, { method, json: o.json, query: o.query, auth: o.auth, signal: o.signal, headers });
  }
  const epoch = res.headers.get('x-server-epoch');
  if (epoch) emit({ epoch });
  if (res.status === 426) emit({ upgradeRequired: true });
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('json') ? await res.json() : await res.text()) as T;
}

export const api = {
  get: <T = unknown>(p: string, o?: ReqOpts) => request<T>('GET', p, o),
  post: <T = unknown>(p: string, json?: unknown, o?: ReqOpts) => request<T>('POST', p, { ...o, json }),
  patch: <T = unknown>(p: string, json?: unknown, o?: ReqOpts) => request<T>('PATCH', p, { ...o, json }),
  put: <T = unknown>(p: string, json?: unknown, o?: ReqOpts) => request<T>('PUT', p, { ...o, json }),
  del: <T = unknown>(p: string, json?: unknown, o?: ReqOpts) => request<T>('DELETE', p, { ...o, json }),
  /** For blobs (PDF receipts, CSV exports) */
  async blob(p: string, o?: ReqOpts): Promise<Blob> {
    if (!accessToken) await refreshSession();
    const res = await raw(p, { method: 'GET', query: o?.query });
    if (!res.ok) throw await toError(res);
    return res.blob();
  },
  async upload<T = unknown>(p: string, form: FormData): Promise<T> {
    if (!accessToken) await refreshSession();
    const res = await raw(p, { method: 'POST', body: form });
    if (!res.ok) throw await toError(res);
    return res.json() as Promise<T>;
  },
};

export const errMsg = (e: unknown, lang: 'bn' | 'en' = 'bn'): string => {
  if (e instanceof ApiError) return lang === 'bn' ? e.messageBn : e.messageEn;
  return lang === 'bn' ? 'কিছু একটা ভুল হয়েছে।' : 'Something went wrong.';
};
export const errCode = (e: unknown): string | undefined => (e instanceof ApiError ? e.code : undefined);
export type { ErrorCode };
